/* eslint-disable max-lines */
import { IconCheck, IconX } from '@tabler/icons-react';
import {
  AutoPayStatus,
  InitialPremiumPaymentStatus,
  UpdateBenefitPaymentStatusDto,
} from '@zorro/clients';
import {
  VALIDATION_MESSAGES,
  formatDayOfMonthToOrdinal,
  isDefined,
} from '@zorro/common-utils';
import { IconHalfCircle } from '@zorro/shared/assets';
import { DeepReadonly, YesNo } from '@zorro/types';
import { Box, Icon, brand } from '@zorro/web/design-system';
import React from 'react';
import { $enum } from 'ts-enum-util';
import * as yup from 'yup';

import { AgentOrEmployee } from './AgentOrEmployeeInput';

const INITIAL_PREMIUM_SET_UP_DESCRIPTION =
  "Your initial premium payment was set up. We'll mark it completed once we confirm it went through.";
const INITIAL_PREMIUM_COMPLETED_DESCRIPTION =
  'Your first premium payment went through.';
const AUTO_PAY_SET_UP_DESCRIPTION =
  'Auto-pay is on — your premiums will be paid automatically each cycle.';
export const INITIAL_PREMIUM_LEARN_MORE_LINK =
  'https://help.myzorro.co/en/articles/9909428-how-do-i-know-if-my-initial-premium-was-paid-or-if-i-am-set-up-with-autopay';
export const AUTO_PAY_LEARN_MORE_LINK =
  'https://help.myzorro.co/en/articles/9909445-how-do-i-set-up-auto-pay';
const INITIAL_PREMIUM_TOOL_TIP_LABEL =
  "Go to your carrier's website and complete your enrollment's initial premium payment";
const AUTO_PAY_TOOL_TIP_LABEL =
  "Go to your carrier's website and set up your auto pay";

export const MONTHLY_AUTO_PAY_END_OF_MONTH_VALUE = '0';

export const getMonthlyAutoPayDateOptions = () => {
  const options = Array.from({ length: 28 }, (_, index) => ({
    label: (index + 1).toString(),
    value: (index + 1).toString(),
  }));
  options.push({
    label: 'Last day of the month',
    value: MONTHLY_AUTO_PAY_END_OF_MONTH_VALUE,
  });
  return options;
};

export const parseMonthlyAutoPayDateFromOptions = (
  value: string | null | undefined
) => {
  if (value === MONTHLY_AUTO_PAY_END_OF_MONTH_VALUE) {
    return { isMonthlyAutoPayDateEndOfMonth: true, monthlyAutoPayDate: null };
  }
  if (!isDefined(value)) {
    return {
      isMonthlyAutoPayDateEndOfMonth: undefined,
      monthlyAutoPayDate: null,
    };
  }
  return {
    isMonthlyAutoPayDateEndOfMonth: false,
    monthlyAutoPayDate: Number.parseInt(value),
  };
};

export const formatMonthlyAutoPayRecurrence = (
  monthlyAutoPayDate: number | null | undefined,
  isMonthlyAutoPayDateEndOfMonth: boolean | undefined
): string | null => {
  if (isMonthlyAutoPayDateEndOfMonth) return 'the last day of the month';
  if (isDefined(monthlyAutoPayDate)) {
    return `the ${formatDayOfMonthToOrdinal(monthlyAutoPayDate)}`;
  }
  return null;
};

// Form-side progress for the initial premium: the 3-state lifecycle the UI picks.
// Distinct from the DB enum InitialPremiumPaymentStatus, which also carries the
// _BY_AGENT / _BY_EMPLOYEE actor. Auto-pay has no "Completed" (ongoing arrangement).
export enum InitialPremiumProgress {
  NOT_SET_UP = 'NOT_SET_UP',
  SET_UP = 'SET_UP',
  COMPLETED = 'COMPLETED',
}

export const paymentStatusSchema = yup.object({
  initialPremiumHandler: yup
    .mixed<AgentOrEmployee>()
    .oneOf(
      Object.values(AgentOrEmployee),
      VALIDATION_MESSAGES.paymentHandlerRequired
    )
    .required(VALIDATION_MESSAGES.paymentHandlerRequired),
  initialPremiumCompleted: yup
    .mixed<InitialPremiumProgress>()
    .oneOf(
      Object.values(InitialPremiumProgress),
      VALIDATION_MESSAGES.wasPaymentCompletedRequired
    )
    .required(VALIDATION_MESSAGES.wasPaymentCompletedRequired),
  autoPaySetter: yup
    .mixed<AgentOrEmployee>()
    .oneOf(
      Object.values(AgentOrEmployee),
      VALIDATION_MESSAGES.autoPaySetterRequired
    )
    .required(VALIDATION_MESSAGES.autoPaySetterRequired),
  autoPayCompleted: yup
    .mixed<YesNo>()
    .oneOf(Object.values(YesNo), VALIDATION_MESSAGES.hasAutoPayBeenSetUp)
    .required(VALIDATION_MESSAGES.hasAutoPayBeenSetUp),
  monthlyAutoPayDate: yup
    .string()
    .nullable()
    .optional()
    .when('autoPayCompleted', {
      is: YesNo.YES,
      then: (schema) => schema.required('Monthly auto-pay date is required'),
      otherwise: (schema) => schema.nullable().optional(),
    }),
});

export type PaymentStatusFormOutput = yup.InferType<typeof paymentStatusSchema>;
export type PaymentStatusFormInput = PaymentStatusFormOutput;

const PAYMENT_ICON_BY_STATE = {
  done: { backgroundColor: brand.zorroGreen300, icon: IconCheck },
  inProgress: { backgroundColor: brand.zorroYolk400, icon: IconHalfCircle },
  notDone: { backgroundColor: brand.zorroFire300, icon: IconX },
} as const;

export const getPaymentStatusIcon = (
  state: keyof typeof PAYMENT_ICON_BY_STATE
) => {
  const { backgroundColor, icon } = PAYMENT_ICON_BY_STATE[state];
  return (
    <Box
      bg={backgroundColor}
      w="2rem"
      h="2rem"
      style={(theme) => ({
        borderRadius: theme.spacing.xxs,
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
      })}
    >
      <Icon icon={icon} />
    </Box>
  );
};

type StatusConfig = {
  label: string;
  icon: React.JSX.Element;
  description: string | null;
  learnMoreLink: string | null;
  toolTipLabel: string | null;
  shouldShowMarkAsDone: boolean;
};

const getInitialPremiumNotSetUpByAgentCopy = (handler: string) => ({
  label: `${handler} is setting up your initial premium payment`,
  description: `${handler} is handling the first payment that activates your coverage. Nothing needed from you.`,
});

const getAutoPayNotSetUpByAgentCopy = (handler: string) => ({
  label: `${handler} is setting up your auto-pay`,
  description: `${handler} is turning on automatic premium payments. Nothing needed from you.`,
});

const getInitialPremiumPaymentConfigEmployee = (
  initialPremiumPaymentStatus: InitialPremiumPaymentStatus,
  isZorroEnrollmentTeam: boolean
): StatusConfig =>
  $enum.mapValue(initialPremiumPaymentStatus).with({
    [InitialPremiumPaymentStatus.COMPLETED_BY_EMPLOYEE]: {
      label: 'Initial premium payment completed',
      icon: getPaymentStatusIcon('done'),
      description: INITIAL_PREMIUM_COMPLETED_DESCRIPTION,
      learnMoreLink: INITIAL_PREMIUM_LEARN_MORE_LINK,
      toolTipLabel: null,
      shouldShowMarkAsDone: false,
    },
    [InitialPremiumPaymentStatus.COMPLETED_BY_AGENT]: {
      label: 'Initial premium payment completed',
      icon: getPaymentStatusIcon('done'),
      description: INITIAL_PREMIUM_COMPLETED_DESCRIPTION,
      learnMoreLink: INITIAL_PREMIUM_LEARN_MORE_LINK,
      toolTipLabel: null,
      shouldShowMarkAsDone: false,
    },
    [InitialPremiumPaymentStatus.NOT_SET_UP_BY_AGENT]: {
      ...getInitialPremiumNotSetUpByAgentCopy(
        isZorroEnrollmentTeam ? 'Zorro' : 'Your enrollment team'
      ),
      icon: getPaymentStatusIcon('notDone'),
      learnMoreLink: null,
      toolTipLabel: null,
      shouldShowMarkAsDone: false,
    },
    [InitialPremiumPaymentStatus.NOT_SET_UP_BY_EMPLOYEE]: {
      label: 'You need to set up your initial premium payment',
      icon: getPaymentStatusIcon('notDone'),
      description:
        "Make your first premium payment on your carrier's site to activate coverage — then mark it done here.",
      learnMoreLink: INITIAL_PREMIUM_LEARN_MORE_LINK,
      toolTipLabel: INITIAL_PREMIUM_TOOL_TIP_LABEL,
      shouldShowMarkAsDone: true,
    },
    [InitialPremiumPaymentStatus.SET_UP_BY_AGENT]: {
      label: 'Initial premium payment set up',
      icon: getPaymentStatusIcon('inProgress'),
      description: INITIAL_PREMIUM_SET_UP_DESCRIPTION,
      learnMoreLink: INITIAL_PREMIUM_LEARN_MORE_LINK,
      toolTipLabel: null,
      shouldShowMarkAsDone: false,
    },
    [InitialPremiumPaymentStatus.SET_UP_BY_EMPLOYEE]: {
      label: 'Initial premium payment set up',
      icon: getPaymentStatusIcon('inProgress'),
      description: INITIAL_PREMIUM_SET_UP_DESCRIPTION,
      learnMoreLink: INITIAL_PREMIUM_LEARN_MORE_LINK,
      toolTipLabel: null,
      shouldShowMarkAsDone: false,
    },
  });

const getAutoPayConfigEmployee = (
  autoPayStatus: AutoPayStatus,
  isZorroEnrollmentTeam: boolean
): StatusConfig =>
  $enum.mapValue(autoPayStatus).with({
    [AutoPayStatus.NOT_SET_UP_BY_AGENT]: {
      ...getAutoPayNotSetUpByAgentCopy(
        isZorroEnrollmentTeam ? 'Zorro' : 'Your enrollment team'
      ),
      icon: getPaymentStatusIcon('notDone'),
      learnMoreLink: null,
      toolTipLabel: null,
      shouldShowMarkAsDone: false,
    },
    [AutoPayStatus.NOT_SET_UP_BY_EMPLOYEE]: {
      label: 'Set up auto-pay',
      icon: getPaymentStatusIcon('notDone'),
      description:
        "Your carrier requires members to set up auto-pay directly in the carrier portal to avoid coverage interruptions.\nTiming varies by carrier. Some allow setup right away, others only once coverage starts. Check your portal now. If auto-pay is available, use your Zorro Pay details to set it up. If not, check again after your plan's start date.",
      learnMoreLink: AUTO_PAY_LEARN_MORE_LINK,
      toolTipLabel: AUTO_PAY_TOOL_TIP_LABEL,
      shouldShowMarkAsDone: true,
    },
    [AutoPayStatus.SET_UP_BY_AGENT]: {
      label: 'Auto-pay is set up',
      icon: getPaymentStatusIcon('done'),
      description: AUTO_PAY_SET_UP_DESCRIPTION,
      learnMoreLink: AUTO_PAY_LEARN_MORE_LINK,
      toolTipLabel: null,
      shouldShowMarkAsDone: false,
    },
    [AutoPayStatus.SET_UP_BY_EMPLOYEE]: {
      label: 'Auto-pay is set up',
      icon: getPaymentStatusIcon('done'),
      description: AUTO_PAY_SET_UP_DESCRIPTION,
      learnMoreLink: AUTO_PAY_LEARN_MORE_LINK,
      toolTipLabel: null,
      shouldShowMarkAsDone: false,
    },
  });

export const initialPremiumPaymentConfigOps: DeepReadonly<{
  [status in InitialPremiumPaymentStatus]: Omit<
    StatusConfig,
    'description' | 'learnMoreLink' | 'toolTipLabel'
  > & { description: null; learnMoreLink: null; toolTipLabel: null };
}> = {
  [InitialPremiumPaymentStatus.COMPLETED_BY_EMPLOYEE]: {
    label: 'Completed by employee',
    icon: getPaymentStatusIcon('done'),
    description: null,
    learnMoreLink: null,
    toolTipLabel: null,
    shouldShowMarkAsDone: false,
  },
  [InitialPremiumPaymentStatus.COMPLETED_BY_AGENT]: {
    label: 'Completed by agent',
    icon: getPaymentStatusIcon('done'),
    description: null,
    learnMoreLink: null,
    toolTipLabel: null,
    shouldShowMarkAsDone: false,
  },
  [InitialPremiumPaymentStatus.NOT_SET_UP_BY_AGENT]: {
    label: 'Awaiting agent',
    icon: getPaymentStatusIcon('notDone'),
    description: null,
    learnMoreLink: null,
    toolTipLabel: null,
    shouldShowMarkAsDone: false,
  },
  [InitialPremiumPaymentStatus.NOT_SET_UP_BY_EMPLOYEE]: {
    label: 'Awaiting employee',
    icon: getPaymentStatusIcon('notDone'),
    description: null,
    learnMoreLink: null,
    toolTipLabel: null,
    shouldShowMarkAsDone: false,
  },
  [InitialPremiumPaymentStatus.SET_UP_BY_AGENT]: {
    label: 'Set up by agent',
    icon: getPaymentStatusIcon('inProgress'),
    description: null,
    learnMoreLink: null,
    toolTipLabel: null,
    shouldShowMarkAsDone: false,
  },
  [InitialPremiumPaymentStatus.SET_UP_BY_EMPLOYEE]: {
    label: 'Set up by employee',
    icon: getPaymentStatusIcon('inProgress'),
    description: null,
    learnMoreLink: null,
    toolTipLabel: null,
    shouldShowMarkAsDone: false,
  },
};

export const autoPayConfigOps: DeepReadonly<{
  [status in AutoPayStatus]: Omit<
    StatusConfig,
    'description' | 'learnMoreLink' | 'toolTipLabel'
  > & { description: null; learnMoreLink: null; toolTipLabel: null };
}> = {
  [AutoPayStatus.NOT_SET_UP_BY_AGENT]: {
    label: 'Awaiting agent',
    icon: getPaymentStatusIcon('notDone'),
    description: null,
    learnMoreLink: null,
    toolTipLabel: null,
    shouldShowMarkAsDone: false,
  },
  [AutoPayStatus.NOT_SET_UP_BY_EMPLOYEE]: {
    label: 'Awaiting employee',
    icon: getPaymentStatusIcon('notDone'),
    description: null,
    learnMoreLink: null,
    toolTipLabel: null,
    shouldShowMarkAsDone: false,
  },
  [AutoPayStatus.SET_UP_BY_AGENT]: {
    label: 'Set up by agent',
    icon: getPaymentStatusIcon('done'),
    description: null,
    learnMoreLink: null,
    toolTipLabel: null,
    shouldShowMarkAsDone: false,
  },
  [AutoPayStatus.SET_UP_BY_EMPLOYEE]: {
    label: 'Set up by employee',
    icon: getPaymentStatusIcon('done'),
    description: null,
    learnMoreLink: null,
    toolTipLabel: null,
    shouldShowMarkAsDone: false,
  },
};

export const getInitialPremiumStatus = (
  initialPremiumPaymentStatus: InitialPremiumPaymentStatus,
  shouldShowMarkAsDone: boolean,
  isZorroEnrollmentTeam: boolean
): StatusConfig => {
  if (!shouldShowMarkAsDone) {
    return initialPremiumPaymentConfigOps[initialPremiumPaymentStatus];
  }
  return getInitialPremiumPaymentConfigEmployee(
    initialPremiumPaymentStatus,
    isZorroEnrollmentTeam
  );
};

export const getAutoPayStatus = (
  autoPayStatus: AutoPayStatus,
  shouldShowMarkAsDone: boolean,
  isZorroEnrollmentTeam: boolean
): StatusConfig => {
  if (!shouldShowMarkAsDone) {
    return autoPayConfigOps[autoPayStatus];
  }
  return getAutoPayConfigEmployee(autoPayStatus, isZorroEnrollmentTeam);
};

// Decodes the DB enum (state + actor) back to the form's {handler, progress}.
const initialPremiumHandlerAndProgress: Record<
  InitialPremiumPaymentStatus,
  { handler: AgentOrEmployee; progress: InitialPremiumProgress }
> = {
  [InitialPremiumPaymentStatus.COMPLETED_BY_AGENT]: {
    handler: AgentOrEmployee.AGENT,
    progress: InitialPremiumProgress.COMPLETED,
  },
  [InitialPremiumPaymentStatus.COMPLETED_BY_EMPLOYEE]: {
    handler: AgentOrEmployee.EMPLOYEE,
    progress: InitialPremiumProgress.COMPLETED,
  },
  [InitialPremiumPaymentStatus.NOT_SET_UP_BY_AGENT]: {
    handler: AgentOrEmployee.AGENT,
    progress: InitialPremiumProgress.NOT_SET_UP,
  },
  [InitialPremiumPaymentStatus.NOT_SET_UP_BY_EMPLOYEE]: {
    handler: AgentOrEmployee.EMPLOYEE,
    progress: InitialPremiumProgress.NOT_SET_UP,
  },
  [InitialPremiumPaymentStatus.SET_UP_BY_AGENT]: {
    handler: AgentOrEmployee.AGENT,
    progress: InitialPremiumProgress.SET_UP,
  },
  [InitialPremiumPaymentStatus.SET_UP_BY_EMPLOYEE]: {
    handler: AgentOrEmployee.EMPLOYEE,
    progress: InitialPremiumProgress.SET_UP,
  },
};

const autoPayHandlerAndCompleted: Record<
  AutoPayStatus,
  { handler: AgentOrEmployee; completed: YesNo.YES | YesNo.NO }
> = {
  [AutoPayStatus.NOT_SET_UP_BY_AGENT]: {
    handler: AgentOrEmployee.AGENT,
    completed: YesNo.NO,
  },
  [AutoPayStatus.NOT_SET_UP_BY_EMPLOYEE]: {
    handler: AgentOrEmployee.EMPLOYEE,
    completed: YesNo.NO,
  },
  [AutoPayStatus.SET_UP_BY_AGENT]: {
    handler: AgentOrEmployee.AGENT,
    completed: YesNo.YES,
  },
  [AutoPayStatus.SET_UP_BY_EMPLOYEE]: {
    handler: AgentOrEmployee.EMPLOYEE,
    completed: YesNo.YES,
  },
};

export const getHandlerAndProgressFromInitialPremiumPaymentStatus = (
  status?: InitialPremiumPaymentStatus | null
) => {
  if (!status) return { handler: undefined, progress: undefined };
  return initialPremiumHandlerAndProgress[status];
};

export const getHandlerAndCompletedFromAutoPayStatus = (
  status?: AutoPayStatus | null
) => {
  if (!status) return { handler: undefined, completed: undefined };
  return autoPayHandlerAndCompleted[status];
};

export enum PaymentHandlerRole {
  EMPLOYEE = 'EMPLOYEE',
  AGENT = 'AGENT',
  SPLIT = 'SPLIT',
}

export const getPaymentHandlerRole = (
  initialPremiumPaymentStatus?: InitialPremiumPaymentStatus | null,
  autoPayStatus?: AutoPayStatus | null
): PaymentHandlerRole => {
  const { handler: initialHandler } =
    getHandlerAndProgressFromInitialPremiumPaymentStatus(
      initialPremiumPaymentStatus
    );
  const { handler: autoPayHandler } =
    getHandlerAndCompletedFromAutoPayStatus(autoPayStatus);

  if (initialHandler === undefined || autoPayHandler === undefined) {
    const isEitherHandledByEmployee =
      initialHandler === AgentOrEmployee.EMPLOYEE ||
      autoPayHandler === AgentOrEmployee.EMPLOYEE;
    return isEitherHandledByEmployee
      ? PaymentHandlerRole.EMPLOYEE
      : PaymentHandlerRole.AGENT;
  }

  return $enum.mapValue(initialHandler).with<PaymentHandlerRole>({
    [AgentOrEmployee.EMPLOYEE]: $enum
      .mapValue(autoPayHandler)
      .with<PaymentHandlerRole>({
        [AgentOrEmployee.EMPLOYEE]: PaymentHandlerRole.EMPLOYEE,
        [AgentOrEmployee.AGENT]: PaymentHandlerRole.SPLIT,
      }),
    [AgentOrEmployee.AGENT]: $enum
      .mapValue(autoPayHandler)
      .with<PaymentHandlerRole>({
        [AgentOrEmployee.AGENT]: PaymentHandlerRole.AGENT,
        [AgentOrEmployee.EMPLOYEE]: PaymentHandlerRole.SPLIT,
      }),
  });
};

// Encodes the form's {handler, progress} into the canonical DB enum member.
// Emits only the new lifecycle values — never the legacy TRUE_*/FALSE_*.
const initialPremiumStatusByProgress: Record<
  InitialPremiumProgress,
  Record<AgentOrEmployee, InitialPremiumPaymentStatus>
> = {
  [InitialPremiumProgress.NOT_SET_UP]: {
    [AgentOrEmployee.AGENT]: InitialPremiumPaymentStatus.NOT_SET_UP_BY_AGENT,
    [AgentOrEmployee.EMPLOYEE]:
      InitialPremiumPaymentStatus.NOT_SET_UP_BY_EMPLOYEE,
  },
  [InitialPremiumProgress.SET_UP]: {
    [AgentOrEmployee.AGENT]: InitialPremiumPaymentStatus.SET_UP_BY_AGENT,
    [AgentOrEmployee.EMPLOYEE]: InitialPremiumPaymentStatus.SET_UP_BY_EMPLOYEE,
  },
  [InitialPremiumProgress.COMPLETED]: {
    [AgentOrEmployee.AGENT]: InitialPremiumPaymentStatus.COMPLETED_BY_AGENT,
    [AgentOrEmployee.EMPLOYEE]:
      InitialPremiumPaymentStatus.COMPLETED_BY_EMPLOYEE,
  },
};

export const getInitialPremiumPaymentStatus = (
  handler: AgentOrEmployee,
  progress: InitialPremiumProgress
): InitialPremiumPaymentStatus =>
  initialPremiumStatusByProgress[progress][handler];

export const getAutoPayStatusFromForm = (
  handler: AgentOrEmployee,
  completed: YesNo.YES | YesNo.NO
): AutoPayStatus => {
  if (handler === AgentOrEmployee.AGENT) {
    return completed === YesNo.YES
      ? AutoPayStatus.SET_UP_BY_AGENT
      : AutoPayStatus.NOT_SET_UP_BY_AGENT;
  }
  return completed === YesNo.YES
    ? AutoPayStatus.SET_UP_BY_EMPLOYEE
    : AutoPayStatus.NOT_SET_UP_BY_EMPLOYEE;
};

export const toUpdateBenefitPaymentStatusDto = ({
  initialPremiumHandler,
  initialPremiumCompleted,
  autoPaySetter,
  autoPayCompleted,
  monthlyAutoPayDate,
}: PaymentStatusFormOutput): UpdateBenefitPaymentStatusDto => ({
  initialPremiumPaymentStatus: getInitialPremiumPaymentStatus(
    initialPremiumHandler,
    initialPremiumCompleted
  ),
  autoPayStatus: getAutoPayStatusFromForm(autoPaySetter, autoPayCompleted),
  ...parseMonthlyAutoPayDateFromOptions(monthlyAutoPayDate),
});
